'use client';

import { NextIntlClientProvider } from 'next-intl';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import enMessages from '../../../messages/en/landing.json';
import koMessages from '../../../messages/ko/landing.json';
import { readStoredValue, writeStoredValue } from '../../lib/storage';

type LandingLocale = 'en' | 'ko';

const messages: Record<LandingLocale, Record<string, unknown>> = {
  en: enMessages as Record<string, unknown>,
  ko: koMessages as Record<string, unknown>,
};

type LandingLocaleContextType = {
  locale: LandingLocale;
  t: (key: string) => string;
  toggleLocale: () => void;
};

const LandingLocaleContext = createContext<LandingLocaleContextType>({
  locale: 'en',
  t: (key: string) => key,
  toggleLocale: () => {},
});

export function useLandingLocale() {
  return useContext(LandingLocaleContext);
}

export function useLandingTranslations() {
  return useContext(LandingLocaleContext).t;
}

const STORAGE_KEY = 'agentteams-landing-locale';

function getInitialLocale(): LandingLocale {
  const stored = readStoredValue(STORAGE_KEY);
  if (stored === 'ko' || stored === 'en') return stored;
  return 'en';
}

export function LandingLocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocale] = useState<LandingLocale>(getInitialLocale);
  const landingPageMessages = messages[locale].landing as { page?: Record<string, unknown> } | undefined;
  const pageMessages = useMemo(() => landingPageMessages?.page ?? {}, [landingPageMessages?.page]);

  const toggleLocale = useCallback(() => {
    setLocale((prev) => {
      const next = prev === 'en' ? 'ko' : 'en';
      writeStoredValue(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const t = useCallback(
    (key: string) => {
      const value = key.split('.').reduce<unknown>((current, part) => {
        if (!current || typeof current !== 'object') return undefined;
        return (current as Record<string, unknown>)[part];
      }, pageMessages);

      return typeof value === 'string' ? value : key;
    },
    [pageMessages],
  );

  const contextValue = useMemo<LandingLocaleContextType>(
    () => ({ locale, t, toggleLocale }),
    [locale, t, toggleLocale],
  );

  return (
    <LandingLocaleContext.Provider value={contextValue}>
      <NextIntlClientProvider locale={locale} messages={messages[locale]} timeZone="Asia/Seoul">
        {children}
      </NextIntlClientProvider>
    </LandingLocaleContext.Provider>
  );
}
